import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/esm/Add';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import EditIcon from '@mui/icons-material/esm/Edit';
import ChevronRightIcon from '@mui/icons-material/esm/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/esm/ExpandMore';
import UndoIcon from '@mui/icons-material/esm/Undo';
import VisibilityIcon from '@mui/icons-material/esm/Visibility';
import { AppDispatch, RootState } from '../../store';
import {
  fetchPurchaseOrders,
  fetchGoodsReceipts,
  fetchAtRisk,
  fetchProcurementCatalog,
  fetchSupplierSlippage,
} from '../../store/slices/procurementSlice';
import { PageHeader } from '../../components/layout';
import RoleTaskQueue from '../../components/workspaces/RoleTaskQueue';
import roleWorkspaceService, { RoleTaskSourceLink } from '../../services/api/roleWorkspaceService';
import resourceService, { InventorySummaryRow } from '../../services/api/resourceService';
import procurementService, {
  AtRiskTaskResource,
  PurchaseOrder,
  PurchaseOrderStatus,
  GoodsReceipt,
  GoodsReceiptStatus,
  SupplierSlippageRow,
} from '../../services/api/procurementService';

type TabKey = 'home' | 'tasks' | 'pos' | 'grns' | 'at-risk' | 'suppliers';

interface ProcurementPageProps {
  fieldMode?: boolean;
  view?: TabKey;
}

const PO_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  sent: 'Sent',
  confirmed: 'Confirmed',
  partial_received: 'Partial received',
  received: 'Received',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const PO_STATUS_COLORS: Record<PurchaseOrderStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  draft: 'default',
  pending_approval: 'warning',
  approved: 'info',
  sent: 'info',
  confirmed: 'info',
  partial_received: 'warning',
  received: 'success',
  closed: 'success',
  cancelled: 'error',
};

const GRN_STATUS_LABELS: Record<GoodsReceiptStatus, string> = {
  draft: 'Draft',
  pending_acceptance: 'Pending acceptance',
  accepted: 'Accepted',
  rejected: 'Rejected',
  disputed: 'Disputed',
};

const PO_STATUSES: PurchaseOrderStatus[] = [
  'draft',
  'pending_approval',
  'approved',
  'sent',
  'confirmed',
  'partial_received',
  'received',
  'closed',
  'cancelled',
];

const RISK_STATUS_COLORS: Record<AtRiskTaskResource['procurementStatus'], 'default' | 'success' | 'warning' | 'error'> = {
  on_hand: 'success',
  on_order_in_time: 'default',
  on_order_late: 'warning',
  not_ordered: 'error',
};

interface PoLineForm {
  resourceId: string;
  unitOfMeasure: string;
  quantityOrdered: number;
  unitCost: number;
  expectedDeliveryDate: string;
}

function formatDate(value?: string | null): string {
  if (!value) return '-';
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

const monthKeyFor = (value?: string | null) => {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return 'undated';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabelFor = (key: string) => {
  if (key === 'undated') return 'No date';
  const d = new Date(`${key}-01T00:00:00`);
  return Number.isNaN(d.getTime()) ? key : d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

function dateInputFromToday(offsetDays: number): string {
  const date = new Date();
  date.setDate(date.getDate() + Math.max(0, Math.round(offsetDays)));
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function formatCurrency(value: number): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD', maximumFractionDigits: 0 }).format(value);
}

function formatQuantity(value: number, unit?: string | null): string {
  const qty = Number.isFinite(value) ? new Intl.NumberFormat('en-JM', { maximumFractionDigits: 2 }).format(value) : '-';
  return unit ? `${qty} ${unit}` : qty;
}

const humanize = (value?: string | null) => (value ? value.replace(/_/g, ' ') : '');

function daysSlipped(po: PurchaseOrder): number | null {
  if (!po.expectedDeliveryDate || ['received', 'closed', 'cancelled'].includes(po.status)) return null;
  const expected = new Date(po.expectedDeliveryDate).getTime();
  if (Number.isNaN(expected)) return null;
  return Math.max(0, Math.floor((Date.now() - expected) / (1000 * 60 * 60 * 24)));
}

function percentReceived(po: PurchaseOrder): number | null {
  if (!po.items?.length) return null;
  const totalOrdered = po.items.reduce((sum, item) => sum + Number(item.quantityOrdered || 0), 0);
  if (totalOrdered === 0) return null;
  const totalReceived = po.items.reduce((sum, item) => sum + Number(item.quantityReceived || 0), 0);
  return Math.round((totalReceived / totalOrdered) * 100);
}

function newLine(): PoLineForm {
  return {
    resourceId: '',
    unitOfMeasure: '',
    quantityOrdered: 1,
    unitCost: 0,
    expectedDeliveryDate: '',
  };
}

function mergePoLineForms(lines: PoLineForm[]): PoLineForm[] {
  const merged = new Map<string, PoLineForm>();
  for (const line of lines) {
    if (!line.resourceId || Number(line.quantityOrdered) <= 0) continue;
    const existing = merged.get(line.resourceId);
    if (!existing) {
      merged.set(line.resourceId, { ...line, quantityOrdered: Number(line.quantityOrdered) });
      continue;
    }
    merged.set(line.resourceId, {
      ...existing,
      quantityOrdered: Number(existing.quantityOrdered) + Number(line.quantityOrdered),
      unitCost: Number(line.unitCost || existing.unitCost || 0),
      expectedDeliveryDate: [existing.expectedDeliveryDate, line.expectedDeliveryDate].filter(Boolean).sort()[0] ?? '',
    });
  }
  return [...merged.values()];
}

/** Progress derived purely from the real PO lifecycle status and received quantities. */
const statusProgress = (po: PurchaseOrder) => {
  const sourced = po.items?.length
    ? Math.min(100, Math.round(((po.items.filter((item) => item.resourceId).length || 0) / po.items.length) * 100))
    : 0;
  const approved = ['approved', 'sent', 'confirmed', 'partial_received', 'received', 'closed'].includes(po.status) ? 100 : po.status === 'pending_approval' ? 50 : 0;
  const shipped = ['sent', 'confirmed', 'partial_received', 'received', 'closed'].includes(po.status) ? 100 : 0;
  const delivered = percentReceived(po) ?? (['received', 'closed'].includes(po.status) ? 100 : po.status === 'partial_received' ? 50 : 0);
  const paid = po.status === 'closed' ? 100 : 0;
  return { sourced, approved, paid, shipped, delivered };
};

/** Shortfall for an at-risk row: prefer the newer backend coverage field when present. */
const shortfallOf = (row: AtRiskTaskResource): number =>
  row.procurementShortfallQuantity != null && !Number.isNaN(Number(row.procurementShortfallQuantity))
    ? Number(row.procurementShortfallQuantity)
    : Number(row.shortage || 0);

const procurementSpecOf = (row?: AtRiskTaskResource | null): string | null => {
  const text = String(row?.procurementSpecification ?? '').trim();
  return text.length > 0 ? text : null;
};

const NOT_ON_FILE = 'Not on file';

const supplierHistoryLabel = (slippage?: SupplierSlippageRow): string =>
  slippage
    ? `${slippage.orderCount ?? 0} POs · ${slippage.openOrderCount ?? 0} open · ${slippage.lateOpenOrderCount ?? 0} late`
    : 'No purchase order history yet';

const supplierReceiptTimingLabel = (slippage?: SupplierSlippageRow): string =>
  slippage && slippage.receiptCount > 0
    ? `${slippage.onTimeReceiptCount} receipts on time · ${slippage.lateReceiptCount} late · worst slip ${slippage.worstSlipDays} days`
    : 'No accepted receipt timing yet';

const RESOURCE_CATEGORIES = ['Material', 'Supplies', 'Tools', 'Equipment'] as const;
type ResourceCategory = (typeof RESOURCE_CATEGORIES)[number];

function resourceTypeOf(name?: string | null, code?: string | null): ResourceCategory {
  const text = `${name || ''} ${code || ''}`.toLowerCase();
  if (/\b(tool|tools|drill|saw|grinder|trowel|hammer|wheelbarrow|barrow|ladder|float|shovel|spade)\b/.test(text)) return 'Tools';
  if (/\b(supply|supplies|ppe|glove|gloves|nail|nails|screw|screws|fixing|fixings|tape|blade|disc|tie wire|wire)\b/.test(text)) return 'Supplies';
  if (/\b(equipment|excavator|mixer|compactor|roller|crane|generator|pump|vibrator|scaffold|hoist)\b/.test(text)) return 'Equipment';
  return 'Material';
}

export default function ProcurementPage({ fieldMode = false, view }: ProcurementPageProps) {
  const dispatch = useDispatch() as AppDispatch;
  const navigate = useNavigate();
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const { purchaseOrders, goodsReceipts, atRisk, catalog, supplierSlippage, loading, error } =
    useSelector((state: RootState) => state.procurement);
  const [tab, setTab] = useState<TabKey>('pos');
  const activeTab = fieldMode ? (view ?? 'home') : tab;
  const [roleTasks, setRoleTasks] = useState<RoleTaskSourceLink[]>([]);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | ''>('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [expectedFrom, setExpectedFrom] = useState('');
  const [expectedTo, setExpectedTo] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [lastCreatedPoId, setLastCreatedPoId] = useState<string | null>(null);
  const [undoingId, setUndoingId] = useState<string | null>(null);
  const [historyFrom, setHistoryFrom] = useState('');
  const [historyTo, setHistoryTo] = useState('');
  const [historyCategory, setHistoryCategory] = useState<ResourceCategory | ''>('');
  const [historySupplier, setHistorySupplier] = useState('');
  const [atRiskDetail, setAtRiskDetail] = useState<AtRiskTaskResource | null>(null);
  const [receiptSupplier, setReceiptSupplier] = useState<any | null>(null);
  const [receiptSearch, setReceiptSearch] = useState('');
  const [materialsToRecoup, setMaterialsToRecoup] = useState<InventorySummaryRow[]>([]);
  const [recoupDetail, setRecoupDetail] = useState<InventorySummaryRow | null>(null);
  const [materialDialog, setMaterialDialog] = useState<{
    open: boolean;
    lineIndex: number;
    name: string;
    unitOfMeasure: string;
    resourceType: 'material' | 'tool' | 'equipment' | 'supply';
    category: string;
    unitCost: number;
  }>({
    open: false,
    lineIndex: -1,
    name: '',
    unitOfMeasure: '',
    resourceType: 'material',
    category: '',
    unitCost: 0,
  });
  const [poForm, setPoForm] = useState({
    supplierId: '',
    poNumber: '',
    notes: '',
    items: [newLine()],
  });
  const supplierById = useMemo(
    () => new Map((catalog?.suppliers ?? []).map((supplier) => [supplier.id, supplier])),
    [catalog]
  );

  const resourceById = useMemo(
    () => new Map((catalog?.resources ?? []).map((resource) => [resource.id, resource])),
    [catalog]
  );
  const unitOptions = useMemo(() => {
    const units = new Set((catalog?.resources ?? [])
      .map((resource) => resource.unitOfMeasure?.trim())
      .filter(Boolean) as string[]);
    ['each', 'm', 'm2', 'm3', 'kg', 'tonne', 'bag', 'bundle', 'box', 'roll', 'length', 'sheet'].forEach((unit) => units.add(unit));
    return [...units].sort((a, b) => a.localeCompare(b));
  }, [catalog]);

  const currentPo = purchaseOrders[0] ?? null;

  const receiptsForSupplier = (supplierId: string) =>
    goodsReceipts
      .filter((receipt) => receipt.supplierId === supplierId)
      .sort((a, b) => new Date(b.receivedAt || b.createdAt || 0).getTime() - new Date(a.receivedAt || a.createdAt || 0).getTime());

  const receiptTotals = (receipt: GoodsReceipt) =>
    (receipt.items ?? []).reduce(
      (totals, item) => ({
        received: totals.received + Number(item.quantityReceived || 0),
        accepted: totals.accepted + Number(item.quantityAccepted || 0),
        rejected: totals.rejected + Number(item.quantityRejected || 0),
      }),
      { received: 0, accepted: 0, rejected: 0 }
    );
  const selectedSupplierReceipts = receiptSupplier ? receiptsForSupplier(receiptSupplier.id) : [];
  const visibleSupplierReceipts = selectedSupplierReceipts.filter((receipt) => {
    const query = receiptSearch.trim().toLowerCase();
    if (!query) return true;
    return [
      receipt.grnNumber,
      receipt.purchaseOrder?.poNumber,
      receipt.purchaseOrderId,
      receipt.status,
      receipt.notes,
      formatDate(receipt.receivedAt),
      formatDate(receipt.acceptedAt),
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });
  const goodsReceiptsByPo = useMemo(() => {
    const map = new Map<string, GoodsReceipt[]>();
    goodsReceipts.forEach((receipt) => {
      map.set(receipt.purchaseOrderId, [...(map.get(receipt.purchaseOrderId) ?? []), receipt]);
    });
    map.forEach((receipts, poId) => {
      map.set(poId, receipts.sort((a, b) => new Date(b.receivedAt || b.createdAt || 0).getTime() - new Date(a.receivedAt || a.createdAt || 0).getTime()));
    });
    return map;
  }, [goodsReceipts]);
  const purchaseOrderById = useMemo(() => new Map(purchaseOrders.map((po) => [po.id, po])), [purchaseOrders]);
  const latestReceiptDateForPo = (po: PurchaseOrder) => goodsReceiptsByPo.get(po.id)?.[0]?.receivedAt ?? null;
  const historyDateForPo = (po: PurchaseOrder) => latestReceiptDateForPo(po) || po.createdAt || po.expectedDeliveryDate || null;
  const receiptDateFor = (receipt: GoodsReceipt) => receipt.receivedAt || receipt.acceptedAt || receipt.createdAt || null;
  const receiptValue = (receipt: GoodsReceipt) => {
    const po = purchaseOrderById.get(receipt.purchaseOrderId);
    return (receipt.items ?? []).reduce((total, item) => {
      const poItem = po?.items?.find((line) => line.id === item.purchaseOrderItemId);
      const unitCost = Number(item.unitCost ?? poItem?.unitCost ?? 0);
      return total + Number(item.quantityAccepted || item.quantityReceived || 0) * unitCost;
    }, 0);
  };
  const groupReceiptsByMonth = (receipts: GoodsReceipt[]) => receipts
    .slice()
    .sort((a, b) => new Date(receiptDateFor(b) || 0).getTime() - new Date(receiptDateFor(a) || 0).getTime())
    .reduce<Array<{ key: string; label: string; rows: GoodsReceipt[]; value: number }>>((groups, receipt) => {
      const key = monthKeyFor(receiptDateFor(receipt));
      let group = groups.find((entry) => entry.key === key);
      if (!group) {
        group = { key, label: monthLabelFor(key), rows: [], value: 0 };
        groups.push(group);
      }
      group.rows.push(receipt);
      group.value += receiptValue(receipt);
      return groups;
    }, []);
  const receiptGroups = useMemo(() => groupReceiptsByMonth(goodsReceipts), [goodsReceipts, purchaseOrderById]);

  const atRiskPo = useMemo(() => {
    if (!atRiskDetail) return null;
    const direct = atRiskDetail.purchaseOrderId
      ? purchaseOrders.find((po) => po.id === atRiskDetail.purchaseOrderId)
      : undefined;
    if (direct) return direct;
    return [...purchaseOrders]
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
      .find((po) => po.status !== 'cancelled' && (po.items ?? []).some((item) => item.resourceId === atRiskDetail.resourceId)) || null;
  }, [purchaseOrders, atRiskDetail]);

  const atRiskResource = atRiskDetail ? resourceById.get(atRiskDetail.resourceId) : undefined;
  const atRiskType = atRiskDetail ? resourceTypeOf(atRiskResource?.name || atRiskDetail.resourceName, atRiskResource?.resourceCode) : null;
  const atRiskSupplier = atRiskPo
    ? supplierById.get(atRiskPo.supplierId) || atRiskPo.supplier
    : atRiskResource?.preferredSupplierId
      ? supplierById.get(atRiskResource.preferredSupplierId)
      : undefined;

  const loadData = () => {
    if (!activeProjectId) return;
    dispatch(fetchPurchaseOrders({
      projectId: activeProjectId,
      status: statusFilter || undefined,
      supplierId: supplierFilter || undefined,
      expectedDeliveryFrom: expectedFrom || undefined,
      expectedDeliveryTo: expectedTo || undefined,
      limit: 50,
    }));
    dispatch(fetchGoodsReceipts({ projectId: activeProjectId, limit: 50 }));
    dispatch(fetchAtRisk({ projectId: activeProjectId, withinDays: 14 }));
    dispatch(fetchSupplierSlippage({ projectId: activeProjectId, days: 90 }));
    resourceService.getInventorySummary()
      .then((rows) => setMaterialsToRecoup(rows.filter((row) => {
        const total = Number(row.totalQuantity || 0);
        const resourceType = String(row.resourceType || '').toLowerCase();
        return resourceType !== 'labour' && total > 0 && (Number(row.quantityInUse || 0) / total) >= 0.75;
      })))
      .catch((err) => setLocalError(err.response?.data?.error || err.message || 'Failed to load material reorder signals'));
    roleWorkspaceService.procurement(activeProjectId)
      .then((workspace) => setRoleTasks(workspace.tasks))
      .catch((err) => setLocalError(err.response?.data?.error || err.message || 'Failed to load procurement role tasks'));
  };

  useEffect(() => {
    dispatch(fetchProcurementCatalog());
  }, [dispatch]);

  useEffect(() => {
    setAtRiskDetail(null);
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [dispatch, activeProjectId, statusFilter, supplierFilter, expectedFrom, expectedTo]);

  const updateLine = (index: number, patch: Partial<PoLineForm>) => {
    setPoForm((current) => ({
      ...current,
      items: current.items.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    }));
  };

  const openCreateMaterialDialog = (lineIndex: number, name = '') => {
    setMaterialDialog({
      open: true,
      lineIndex,
      name,
      unitOfMeasure: unitOptions[0] ?? 'each',
      resourceType: 'material',
      category: '',
      unitCost: 0,
    });
  };

  const closeCreateMaterialDialog = () => {
    setMaterialDialog((current) => ({ ...current, open: false }));
  };

  const createMaterialForLine = async () => {
    try {
      setSaving(true);
      setLocalError(null);
      const created = await resourceService.createResource({
        name: materialDialog.name,
        resourceType: materialDialog.resourceType,
        category: materialDialog.category || undefined,
        unitOfMeasure: materialDialog.unitOfMeasure,
        unitCost: Number(materialDialog.unitCost || 0),
      });
      updateLine(materialDialog.lineIndex, {
        resourceId: created.id,
        unitOfMeasure: created.unitOfMeasure,
        unitCost: Number(created.unitCost || materialDialog.unitCost || 0),
        expectedDeliveryDate: expectedDateForResource(created.id, poForm.supplierId),
      });
      closeCreateMaterialDialog();
      dispatch(fetchProcurementCatalog());
    } catch (err: any) {
      setLocalError(err.response?.data?.error || err.response?.data?.message || 'Failed to create material');
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePo = async () => {
    if (!activeProjectId) return;
    try {
      setSaving(true);
      setLocalError(null);
      const validLines = mergePoLineForms(poForm.items.filter((line) => line.resourceId && line.quantityOrdered > 0));
      await Promise.all(validLines.map((line) => {
        const resource = resourceById.get(line.resourceId);
        if (!line.unitOfMeasure || !resource || resource.unitOfMeasure === line.unitOfMeasure) return Promise.resolve();
        return resourceService.updateResource(line.resourceId, { unitOfMeasure: line.unitOfMeasure });
      }));
      const expectedDeliveryDate = validLines
        .map((line) => line.expectedDeliveryDate)
        .filter(Boolean)
        .sort()[0];
      const created = await procurementService.createPurchaseOrder({
        projectId: activeProjectId,
        supplierId: poForm.supplierId,
        poNumber: poForm.poNumber || undefined,
        expectedDeliveryDate,
        notes: poForm.notes || undefined,
        items: validLines
          .map((line) => ({
            resourceId: line.resourceId,
            quantityOrdered: Number(line.quantityOrdered),
            unitCost: Number(line.unitCost || 0),
            expectedDeliveryDate: line.expectedDeliveryDate,
          })),
      });
      setCreateOpen(false);
      setPoForm({ supplierId: '', poNumber: '', notes: '', items: [newLine()] });
      if (created?.id) {
        setLastCreatedPoId(created.id);
        setExpandedRow(`uns-${created.id}`);
      }
      loadData();
    } catch (err: any) {
      setLocalError(err.response?.data?.error || err.response?.data?.message || 'Failed to create purchase order');
    } finally {
      setSaving(false);
    }
  };

  /** Prefill the create dialog from an at-risk shortage row. */
  const startOrderForShortage = (row: AtRiskTaskResource) => {
    const resource = resourceById.get(row.resourceId);
    const shortfall = Math.max(1, shortfallOf(row) || 1);
    const supplierId = resource?.preferredSupplierId && supplierById.has(resource.preferredSupplierId) ? resource.preferredSupplierId : '';
    setPoForm({
      supplierId,
      poNumber: '',
      notes: `Covering shortage for ${row.taskCode || row.taskTitle || row.taskId}`,
      items: [{
        resourceId: row.resourceId,
        unitOfMeasure: resource?.unitOfMeasure || '',
        quantityOrdered: shortfall,
        unitCost: Number(resource?.unitCost || 0),
        expectedDeliveryDate: expectedDateForResource(row.resourceId, supplierId),
      }],
    });
    setAtRiskDetail(null);
    setCreateOpen(true);
  };

  const undoOrder = async (poId: string) => {
    try {
      setUndoingId(poId);
      setLocalError(null);
      await procurementService.cancelPurchaseOrder(poId, 'Order undone from field mode');
      if (lastCreatedPoId === poId) setLastCreatedPoId(null);
      setExpandedRow((current) => (current?.endsWith(poId) ? null : current));
      loadData();
    } catch (err: any) {
      setLocalError(err.response?.data?.error || err.response?.data?.message || 'Failed to undo the order');
    } finally {
      setUndoingId(null);
    }
  };

  const hasValidPo = Boolean(
    activeProjectId &&
    poForm.supplierId &&
    poForm.items.some((line) => line.resourceId && Number(line.quantityOrdered) > 0) &&
    poForm.items
      .filter((line) => line.resourceId && Number(line.quantityOrdered) > 0)
      .every((line) => Boolean(line.expectedDeliveryDate) && Boolean(line.unitOfMeasure.trim()))
  );
  const selectedPoResourceIds = poForm.items.map((line) => line.resourceId).filter(Boolean);
  const duplicatePoResourceIds = new Set(selectedPoResourceIds.filter((id, index) => selectedPoResourceIds.indexOf(id) !== index));
  const hasDuplicatePoItems = duplicatePoResourceIds.size > 0;
  const canCreatePo = hasValidPo && !hasDuplicatePoItems;

  const completeRoleTask = async (taskId: string) => {
    setCompletingId(taskId);
    try {
      await roleWorkspaceService.completeTask(taskId);
      loadData();
    } finally {
      setCompletingId(null);
    }
  };

  const toggleRow = (key: string) => setExpandedRow((current) => (current === key ? null : key));

  const activeOrders = useMemo(
    () => purchaseOrders.filter((po) =>
      ['draft', 'pending_approval', 'approved', 'sent', 'confirmed', 'partial_received'].includes(po.status)
    ),
    [purchaseOrders]
  );
  const unscheduledOrders = useMemo(() => activeOrders.filter((po) => !po.expectedDeliveryDate), [activeOrders]);
  const lastCreatedPo = useMemo(
    () => (lastCreatedPoId ? purchaseOrders.find((po) => po.id === lastCreatedPoId && po.status === 'draft') ?? null : null),
    [purchaseOrders, lastCreatedPoId]
  );
  const scheduledOrders = useMemo(() => activeOrders.filter((po) => Boolean(po.expectedDeliveryDate)), [activeOrders]);

  /** Real schedule for the field "Schedule" view: scheduled orders grouped by expected delivery date. */
  const scheduleGroups = useMemo(() => {
    const groups = new Map<string, PurchaseOrder[]>();
    [...scheduledOrders]
      .sort((a, b) => new Date(a.expectedDeliveryDate || 0).getTime() - new Date(b.expectedDeliveryDate || 0).getTime())
      .forEach((po) => {
        const key = formatDate(po.expectedDeliveryDate);
        groups.set(key, [...(groups.get(key) ?? []), po]);
      });
    return [...groups.entries()];
  }, [scheduledOrders]);

  const ordersOverdueCount = purchaseOrders.filter((po) => (daysSlipped(po) ?? 0) > 0).length;
  const openOrdersCount = activeOrders.length;
  const lateShipmentsCount = purchaseOrders.filter((po) =>
    ['sent', 'confirmed', 'partial_received'].includes(po.status) && (daysSlipped(po) ?? 0) > 0
  ).length;
  const usedPercentOf = (row: InventorySummaryRow) => {
    const total = Number(row.totalQuantity || 0);
    return total > 0 ? Math.round((100 * Number(row.quantityInUse || 0)) / total) : 0;
  };
  const criticalRecoupCount = materialsToRecoup.filter((row) => usedPercentOf(row) >= 90 || row.belowSafetyStock).length;

  const supplierForResource = (resourceId: string) => {
    const resource = resourceById.get(resourceId);
    return resource?.preferredSupplierId ? supplierById.get(resource.preferredSupplierId) : undefined;
  };

  const expectedDateForResource = (resourceId: string, supplierId?: string | null) => {
    const resource = resourceById.get(resourceId);
    const supplier = supplierId ? supplierById.get(supplierId) : undefined;
    const leadDays = Number(supplier?.leadTimeDays ?? resource?.leadTimeDays ?? 7);
    return dateInputFromToday(Number.isFinite(leadDays) ? leadDays : 7);
  };

  const shippingTimeForResource = (resourceId: string, supplierId?: string | null) => {
    const receiptLeadDays = goodsReceipts
      .filter((receipt) => !supplierId || receipt.supplierId === supplierId)
      .flatMap((receipt) => {
        const hasResource = (receipt.items ?? []).some((item) => item.resourceId === resourceId);
        if (!hasResource) return [];
        const po = purchaseOrders.find((order) => order.id === receipt.purchaseOrderId);
        const orderedAt = po?.createdAt ? new Date(po.createdAt).getTime() : Number.NaN;
        const receivedAt = receipt.receivedAt ? new Date(receipt.receivedAt).getTime() : Number.NaN;
        if (Number.isNaN(orderedAt) || Number.isNaN(receivedAt) || receivedAt < orderedAt) return [];
        return [Math.max(0, Math.round((receivedAt - orderedAt) / (1000 * 60 * 60 * 24)))];
      });
    if (receiptLeadDays.length) {
      const average = Math.round(receiptLeadDays.reduce((sum, days) => sum + days, 0) / receiptLeadDays.length);
      return `${average} day${average === 1 ? '' : 's'} avg shipping`;
    }

    const openLeadDays = purchaseOrders
      .filter((po) => (!supplierId || po.supplierId === supplierId) && (po.items ?? []).some((item) => item.resourceId === resourceId))
      .flatMap((po) => {
        const orderedAt = po.createdAt ? new Date(po.createdAt).getTime() : Number.NaN;
        const expectedAt = po.expectedDeliveryDate ? new Date(po.expectedDeliveryDate).getTime() : Number.NaN;
        if (Number.isNaN(orderedAt) || Number.isNaN(expectedAt) || expectedAt < orderedAt) return [];
        return [Math.max(0, Math.round((expectedAt - orderedAt) / (1000 * 60 * 60 * 24)))];
      });
    if (openLeadDays.length) {
      const average = Math.round(openLeadDays.reduce((sum, days) => sum + days, 0) / openLeadDays.length);
      return `${average} day${average === 1 ? '' : 's'} expected shipping`;
    }

    const slippage = supplierId ? supplierSlippage.find((row) => row.supplierId === supplierId) : undefined;
    return slippage ? supplierHistoryLabel(slippage) : 'Shipping time not recorded';
  };

  const materialsToRecoupPanel = (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#fff' }}>
      <Stack spacing={1.25}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={900}>Materials to Recoup</Typography>
            <Typography variant="body2" color="text.secondary">
              Stores usage is 75% or higher for materials, tools, equipment, or supplies. Use this list when preparing the next PO.
            </Typography>
          </Box>
          <Chip
            size="small"
            color={criticalRecoupCount > 0 ? 'error' : materialsToRecoup.length > 0 ? 'warning' : 'success'}
            label={`${materialsToRecoup.length} signals`}
          />
        </Stack>
        {materialsToRecoup.length === 0 ? (
          <Alert severity="success">No stock resources are currently above the 75% recoup threshold.</Alert>
        ) : (
          <Stack spacing={1}>
            {materialsToRecoup.slice(0, fieldMode ? 5 : 12).map((row) => {
              const target = row.reorderLevel ?? row.safetyStockLevel ?? 0;
              const shortfall = target > 0 ? Math.max(0, target - Number(row.quantityAvailable || 0)) : Math.max(0, Number(row.quantityInUse || 0));
              const supplier = supplierForResource(row.resourceId);
              const shippingTime = shippingTimeForResource(row.resourceId, supplier?.id);
              const usedPercent = usedPercentOf(row);
              return (
                <Stack
                  key={row.inventoryId}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setRecoupDetail(row)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setRecoupDetail(row);
                    }
                  }}
                  sx={{
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                    '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.light', outlineOffset: 2 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={900}>{row.resourceName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row.resourceCode} - {row.storageLocationName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.35 }}
                    >
                      Supplier: {supplier?.name || 'No preferred supplier'} | {shippingTime}
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5, fontWeight: 900 }}>
                      View details
                    </Typography>
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: 180, md: 220 }, flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary">Usage</Typography>
                    <Typography variant="body2" fontWeight={800}>
                      {usedPercent}% used ({formatQuantity(row.quantityInUse, row.unitOfMeasure)})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Available: {formatQuantity(row.quantityAvailable, row.unitOfMeasure)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: 120, md: 170 }, flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary">Recoup</Typography>
                    <Typography variant="body2" fontWeight={800} color={row.belowSafetyStock ? 'error.main' : 'warning.main'}>
                      {formatQuantity(shortfall, row.unitOfMeasure)}
                    </Typography>
                  </Box>
                </Stack>
              );
            })}
            {fieldMode && materialsToRecoup.length > 5 && (
              <Typography variant="caption" color="text.secondary">
                {materialsToRecoup.length - 5} more material signals in the full procurement view.
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );

  const poCategoryOf = (po: PurchaseOrder): ResourceCategory[] =>
    (po.items ?? []).map((item) => {
      const resource = resourceById.get(item.resourceId);
      return resourceTypeOf(resource?.name || item.description, resource?.resourceCode);
    });

  const historyRows = useMemo(() => {
    const fromTs = historyFrom ? new Date(historyFrom).getTime() : null;
    const toTs = historyTo ? new Date(historyTo).getTime() + 24 * 60 * 60 * 1000 - 1 : null;
    return [...purchaseOrders]
      .sort((a, b) => new Date(historyDateForPo(b) || 0).getTime() - new Date(historyDateForPo(a) || 0).getTime())
      .filter((po) => {
        if (!['received', 'closed'].includes(po.status)) return false;
        const historyDate = new Date(historyDateForPo(po) || 0).getTime();
        if (fromTs !== null && historyDate < fromTs) return false;
        if (toTs !== null && historyDate > toTs) return false;
        if (historySupplier && po.supplierId !== historySupplier) return false;
        if (historyCategory && !poCategoryOf(po).includes(historyCategory)) return false;
        return true;
      });
  }, [purchaseOrders, historyFrom, historyTo, historySupplier, historyCategory, resourceById, goodsReceiptsByPo]);

  const historyGroups = useMemo(() => historyRows.reduce<Array<{ key: string; label: string; rows: PurchaseOrder[]; total: number }>>((groups, po) => {
    const key = monthKeyFor(historyDateForPo(po));
    let group = groups.find((entry) => entry.key === key);
    if (!group) {
      group = { key, label: monthLabelFor(key), rows: [], total: 0 };
      groups.push(group);
    }
    group.rows.push(po);
    group.total += Number(po.totalAmount || 0);
    return groups;
  }, []), [historyRows, goodsReceiptsByPo]);

  const renderReceiptDetails = (receipt: GoodsReceipt) => {
    const po = purchaseOrderById.get(receipt.purchaseOrderId);
    const supplierName = receipt.supplier?.name || po?.supplier?.name || supplierById.get(receipt.supplierId)?.name || receipt.supplierId;
    const totals = receiptTotals(receipt);
    const value = receiptValue(receipt);

    return (
      <Stack spacing={1.25}>
        <Grid container spacing={1}>
          <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Supplier</Typography><Typography fontWeight={800}>{supplierName}</Typography></Grid>
          <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Purchase order</Typography><Typography fontWeight={800}>{po?.poNumber || receipt.purchaseOrder?.poNumber || receipt.purchaseOrderId}</Typography></Grid>
          <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Received</Typography><Typography fontWeight={800}>{formatDate(receipt.receivedAt)}</Typography></Grid>
          <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Accepted</Typography><Typography fontWeight={800}>{formatDate(receipt.acceptedAt)}</Typography></Grid>
          <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Qty received</Typography><Typography fontWeight={800}>{totals.received}</Typography></Grid>
          <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Qty accepted</Typography><Typography fontWeight={800}>{totals.accepted}</Typography></Grid>
          <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Qty rejected</Typography><Typography fontWeight={800}>{totals.rejected}</Typography></Grid>
          <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Receipt value</Typography><Typography fontWeight={800}>{value > 0 ? formatCurrency(value) : '-'}</Typography></Grid>
        </Grid>
        {receipt.notes && (
          <Alert severity="info" sx={{ py: 0.5 }}>{receipt.notes}</Alert>
        )}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
          <Table size="small" sx={{ '& td, & th': { py: 0.75, px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Purchased material</TableCell>
                <TableCell>Storage</TableCell>
                <TableCell align="right">Ordered</TableCell>
                <TableCell align="right">Received</TableCell>
                <TableCell align="right">Accepted</TableCell>
                <TableCell align="right">Rejected</TableCell>
                <TableCell align="right">Unit cost</TableCell>
                <TableCell align="right">Line value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(receipt.items ?? []).map((item) => {
                const poItem = po?.items?.find((line) => line.id === item.purchaseOrderItemId);
                const resource = item.resource || poItem?.resource || resourceById.get(item.resourceId);
                const unit = resource?.unitOfMeasure || poItem?.resource?.unitOfMeasure;
                const unitCost = Number(item.unitCost ?? poItem?.unitCost ?? 0);
                const quantityForValue = Number(item.quantityAccepted || item.quantityReceived || 0);
                const storageName = [
                  item.storageLocation?.name || poItem?.storageLocation?.name,
                  item.storageZone?.name || poItem?.storageZone?.name,
                ].filter(Boolean).join(' / ');
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={900}>{resource?.name || poItem?.description || item.resourceId}</Typography>
                      <Typography variant="caption" color="text.secondary">{resource?.resourceCode || poItem?.description || ''}</Typography>
                      {item.notes && <Typography variant="caption" display="block" color="text.secondary">Note: {item.notes}</Typography>}
                      {item.rejectionReason && <Typography variant="caption" display="block" color="error.main">Rejected: {item.rejectionReason}</Typography>}
                    </TableCell>
                    <TableCell>{storageName || '-'}</TableCell>
                    <TableCell align="right">{formatQuantity(Number(poItem?.quantityOrdered || 0), unit)}</TableCell>
                    <TableCell align="right">{formatQuantity(Number(item.quantityReceived || 0), unit)}</TableCell>
                    <TableCell align="right">{formatQuantity(Number(item.quantityAccepted || 0), unit)}</TableCell>
                    <TableCell align="right">{formatQuantity(Number(item.quantityRejected || 0), unit)}</TableCell>
                    <TableCell align="right">{unitCost > 0 ? formatCurrency(unitCost) : '-'}</TableCell>
                    <TableCell align="right">{unitCost > 0 ? formatCurrency(quantityForValue * unitCost) : '-'}</TableCell>
                  </TableRow>
                );
              })}
              {(receipt.items ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}><Alert severity="info">No purchased material lines are attached to this receipt.</Alert></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );
  };

  const renderReceiptGroups = (groups: Array<{ key: string; label: string; rows: GoodsReceipt[]; value: number }>, emptyMessage: string) => (
    <Stack spacing={1}>
      {groups.map((group) => (
        <Accordion key={`receipt-month-${group.key}`} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ width: '100%', pr: 1 }}>
              <Typography fontWeight={900}>{group.label}</Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${group.rows.length} receipt${group.rows.length === 1 ? '' : 's'}`} />
                <Typography variant="body2" fontWeight={900}>{group.value > 0 ? formatCurrency(group.value) : 'No value recorded'}</Typography>
              </Stack>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {group.rows.map((receipt) => {
                const po = purchaseOrderById.get(receipt.purchaseOrderId);
                const totals = receiptTotals(receipt);
                const supplierName = receipt.supplier?.name || po?.supplier?.name || supplierById.get(receipt.supplierId)?.name || receipt.supplierId;
                return (
                  <Accordion key={`receipt-${receipt.id}`} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} sx={{ width: '100%', pr: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={900}>{receipt.grnNumber}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            PO {po?.poNumber || receipt.purchaseOrder?.poNumber || receipt.purchaseOrderId} - {supplierName}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip size="small" label={GRN_STATUS_LABELS[receipt.status]} />
                          <Chip size="small" variant="outlined" label={`Received ${formatDate(receipt.receivedAt)}`} />
                          <Chip size="small" variant="outlined" label={`${totals.accepted || totals.received} accepted/received`} />
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {renderReceiptDetails(receipt)}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
      {!loading.goodsReceipts && groups.length === 0 && <Alert severity="info">{emptyMessage}</Alert>}
    </Stack>
  );

  const renderCompletedOrderHistory = () => (
    <Stack spacing={1}>
      {historyGroups.map((group) => (
        <Accordion key={group.key} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ width: '100%', pr: 1 }}>
              <Typography fontWeight={900}>{group.label}</Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${group.rows.length} completed order${group.rows.length === 1 ? '' : 's'}`} />
                <Typography variant="body2" fontWeight={900}>{formatCurrency(group.total)}</Typography>
              </Stack>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {group.rows.map((po) => {
                const receivedDate = latestReceiptDateForPo(po);
                return (
                  <Accordion key={`history-${po.id}`} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ width: '100%', pr: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={800}>{po.poNumber}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {po.supplier?.name || supplierById.get(po.supplierId)?.name || po.supplierId} - Received {formatDate(receivedDate)}
                          </Typography>
                        </Box>
                        <Chip size="small" color={PO_STATUS_COLORS[po.status]} label={PO_STATUS_LABELS[po.status]} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={1} sx={{ mb: 1 }}>
                        <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Ordered</Typography><Typography fontWeight={800}>{formatDate(po.createdAt)}</Typography></Grid>
                        <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Received</Typography><Typography fontWeight={800}>{formatDate(receivedDate)}</Typography></Grid>
                        <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Value</Typography><Typography fontWeight={800}>{formatCurrency(Number(po.totalAmount || 0))}</Typography></Grid>
                        <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Status</Typography><Typography fontWeight={800}>{PO_STATUS_LABELS[po.status]}</Typography></Grid>
                      </Grid>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Material</TableCell>
                            <TableCell align="right">Ordered</TableCell>
                            <TableCell align="right">Received</TableCell>
                            <TableCell align="right">Unit cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(po.items ?? []).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Typography fontWeight={800}>{item.resource?.name || item.description || item.resourceId}</Typography>
                                <Typography variant="caption" color="text.secondary">{item.resource?.resourceCode || ''}</Typography>
                              </TableCell>
                              <TableCell align="right">{formatQuantity(Number(item.quantityOrdered || 0), item.resource?.unitOfMeasure)}</TableCell>
                              <TableCell align="right">{formatQuantity(Number(item.quantityReceived || 0), item.resource?.unitOfMeasure)}</TableCell>
                              <TableCell align="right">{formatCurrency(Number(item.unitCost || 0))}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
      {historyGroups.length === 0 && <Alert severity="info">No completed purchase orders match the history filters.</Alert>}
    </Stack>
  );

  const renderOrderRow = (po: PurchaseOrder, keyPrefix: string, label: string, navigateOnPress = false) => {
    const key = `${keyPrefix}-${po.id}`;
    const expanded = expandedRow === key;
    const progress = statusProgress(po);
    const supplierName = po.supplier?.name || supplierById.get(po.supplierId)?.name || po.supplierId;
    return (
      <Paper key={key} variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={() => (navigateOnPress ? navigate(`/field/purchase-orders/${po.id}`) : toggleRow(key))}
          sx={{ px: 1.5, py: 1, cursor: 'pointer', minHeight: 56 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={800} noWrap>{label} # {po.poNumber}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{supplierName}</Typography>
          </Box>
          {po.id === lastCreatedPoId && <Chip size="small" color="info" label="New" />}
          <Chip size="small" color={PO_STATUS_COLORS[po.status]} label={PO_STATUS_LABELS[po.status]} />
          {navigateOnPress
            ? <ChevronRightIcon />
            : <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
        </Stack>
        <Collapse in={expanded} unmountOnExit>
          <Divider />
          <Box sx={{ px: 1.5, py: 1.5 }}>
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">Expected</Typography>
                <Typography variant="body2" fontWeight={700}>{formatDate(po.expectedDeliveryDate)}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">Ordered</Typography>
                <Typography variant="body2" fontWeight={700}>{formatDate(po.createdAt)}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">Value</Typography>
                <Typography variant="body2" fontWeight={700}>{formatCurrency(Number(po.totalAmount || 0))}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">Delivered</Typography>
                <Typography variant="body2" fontWeight={700}>{progress.delivered}%</Typography>
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={po.status === 'draft' ? <EditIcon /> : undefined}
                component={RouterLink}
                to={`/field/purchase-orders/${po.id}`}
                onClick={(event) => event.stopPropagation()}
              >
                {po.status === 'draft' ? 'Edit order' : 'View details'}
              </Button>
              {po.status === 'draft' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<UndoIcon />}
                  disabled={undoingId === po.id}
                  onClick={(event) => { event.stopPropagation(); undoOrder(po.id); }}
                >
                  {undoingId === po.id ? 'Undoing…' : 'Undo order'}
                </Button>
              )}
            </Stack>
          </Box>
        </Collapse>
      </Paper>
    );
  };

  const renderSupplyRow = (row: AtRiskTaskResource, index: number) => {
    const key = `risk-${row.taskId}-${row.resourceId}-${index}`;
    const resource = resourceById.get(row.resourceId);
    const type = resourceTypeOf(resource?.name || row.resourceName, resource?.resourceCode);
    const procurementSpec = procurementSpecOf(row);
    return (
      <Paper key={key} variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" spacing={1} onClick={() => setAtRiskDetail(row)} sx={{ px: 1.5, py: 1, cursor: 'pointer', minHeight: 56 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={800} noWrap>
              Resource # {resource?.resourceCode || row.resourceName || row.resourceId}
              <Typography component="span" color="text.secondary" fontWeight={700}> — {type}</Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{row.resourceName || resource?.name || row.resourceId}</Typography>
            {procurementSpec && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal' }}>
                Procurement specification: {procurementSpec}
              </Typography>
            )}
          </Box>
          <Chip size="small" color={RISK_STATUS_COLORS[row.procurementStatus]} label={row.procurementStatus.replace(/_/g, ' ')} />
          <ChevronRightIcon />
        </Stack>
      </Paper>
    );
  };

  const addSupplierButton = (
    <Tooltip title="Supplier management arrives with the supplier database update">
      <span>
        <Button variant="contained" startIcon={<AddIcon />} disabled>Add supplier</Button>
      </span>
    </Tooltip>
  );

  const supplierField = (value?: string | null) => (
    <Typography variant="body2" color={value ? 'text.primary' : 'text.secondary'}>{value || NOT_ON_FILE}</Typography>
  );

  const suppliersSection = (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" fontWeight={900}>Suppliers Database</Typography>
        {addSupplierButton}
      </Stack>
      <Grid container spacing={2}>
        {(catalog?.suppliers ?? []).map((supplier: any) => {
          const slippage = supplierSlippage.find((row) => row.supplierId === supplier.id);
          const supplierReceipts = receiptsForSupplier(supplier.id);
          return (
            <Grid item xs={12} md={6} key={supplier.id}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography fontWeight={900}>{supplier.name}</Typography>
                    {supplier.isActive != null && (
                      <Chip size="small" color={supplier.isActive ? 'success' : 'default'} label={supplier.isActive ? 'Active' : 'Inactive'} />
                    )}
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">Contact</Typography>{supplierField(supplier.contactPerson)}</Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">Phone</Typography>{supplierField(supplier.phone)}</Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">Email</Typography>{supplierField(supplier.email)}</Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">Address</Typography>{supplierField(supplier.address)}</Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">Payment terms</Typography>{supplierField(supplier.paymentTerms)}</Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">Delivery history</Typography><Typography variant="body2">{supplierHistoryLabel(slippage)}</Typography></Grid>
                  </Grid>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    disabled={supplierReceipts.length === 0}
                    onClick={() => setReceiptSupplier(supplier)}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    View receipts ({supplierReceipts.length})
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          );
        })}
        {(catalog?.suppliers ?? []).length === 0 && (
          <Grid item xs={12}><Alert severity="info">No suppliers on file yet.</Alert></Grid>
        )}
      </Grid>
    </Stack>
  );

  const FieldMetric = ({ label, value, detail }: { label: string; value: number | string; detail: string }) => (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff', minHeight: 104 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{detail}</Typography>
    </Paper>
  );

  const FieldMenuBar = ({ label, detail, onClick }: { label: string; detail: string; onClick: () => void }) => (
    <Paper variant="outlined" onClick={onClick} sx={{ p: 2, borderRadius: 1, bgcolor: '#fff', cursor: 'pointer' }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900 }}>{label}</Typography>
          <Typography variant="body2" color="text.secondary">{detail}</Typography>
        </Box>
        <ChevronRightIcon />
      </Stack>
    </Paper>
  );

  const historyFilterBar = (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: '#fff' }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight={800}>Filter</Typography>
        <TextField
          size="small"
          type="date"
          label="From"
          value={historyFrom}
          InputLabelProps={{ shrink: true }}
          onChange={(event) => setHistoryFrom(event.target.value)}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={historyTo}
          InputLabelProps={{ shrink: true }}
          onChange={(event) => setHistoryTo(event.target.value)}
          sx={{ width: 150 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>By category</InputLabel>
          <Select value={historyCategory} label="By category" onChange={(event) => setHistoryCategory(event.target.value as ResourceCategory | '')}>
            <MenuItem value="">All categories</MenuItem>
            {RESOURCE_CATEGORIES.map((category) => (
              <MenuItem key={category} value={category}>{category}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>By supplier</InputLabel>
          <Select value={historySupplier} label="By supplier" onChange={(event) => setHistorySupplier(event.target.value)}>
            <MenuItem value="">All suppliers</MenuItem>
            {(catalog?.suppliers ?? []).map((supplier) => (
              <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Paper>
  );

  return (
    <Stack spacing={2}>
      {!fieldMode && (
        <PageHeader
          title="Procurement"
          subtitle="Purchase orders, delivery records, supplier delivery risk, and materials at risk."
        />
      )}

      {(error || localError) && <Alert severity="error">{localError || error}</Alert>}
      {!activeProjectId && <Alert severity="warning">Select an active project to load procurement data.</Alert>}

      {!fieldMode && <Paper sx={{ p: 2, borderRadius: 1 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(event) => setStatusFilter(event.target.value as PurchaseOrderStatus | '')}>
              <MenuItem value="">All statuses</MenuItem>
              {PO_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>{PO_STATUS_LABELS[status]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Supplier</InputLabel>
            <Select value={supplierFilter} label="Supplier" onChange={(event) => setSupplierFilter(event.target.value)}>
              <MenuItem value="">All suppliers</MenuItem>
              {(catalog?.suppliers ?? []).map((supplier) => (
                <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="Expected from"
            value={expectedFrom}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setExpectedFrom(event.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="Expected to"
            value={expectedTo}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setExpectedTo(event.target.value)}
          />
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            disabled={!activeProjectId}
          >
            Create PO
          </Button>
        </Stack>
      </Paper>}

      {fieldMode && (
        <Stack spacing={2.5}>
          {activeTab === 'home' && (
            <Stack spacing={2}>
              <Typography variant="h4" sx={{ fontSize: 30, fontWeight: 900, letterSpacing: 0 }}>Procurement</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
                <FieldMetric label="Orders overdue" value={ordersOverdueCount} detail="Purchase orders past due date" />
                <FieldMetric label="Open orders" value={openOrdersCount} detail="Orders still in the buying cycle" />
                <FieldMetric label="Late shipments" value={lateShipmentsCount} detail="Orders shipped but late to site" />
                <FieldMetric label="At risk stocks" value={atRisk.length} detail="Items at risk to field supply" />
              </Box>
              {materialsToRecoupPanel}

              {currentPo && (
                <>
                  <FieldMenuBar
                    label={`PO # ${currentPo.poNumber}`}
                    detail="Current PO details"
                    onClick={() => navigate(`/field/purchase-orders/${currentPo.id}`)}
                  />
                  <Box>
                    <Stack direction="row" justifyContent="space-between"><Typography sx={{ fontWeight: 900 }}>Current PO order tracker</Typography><Typography>{statusProgress(currentPo).delivered}%</Typography></Stack>
                    <LinearProgress variant="determinate" value={statusProgress(currentPo).delivered} sx={{ height: 8, borderRadius: 1, mt: 0.75 }} />
                  </Box>
                </>
              )}
              {!currentPo && !loading.purchaseOrders && (
                <Alert severity="info">No purchase orders on this project yet.</Alert>
              )}

              <FieldMenuBar label="Purchase orders" detail={`${purchaseOrders.length} purchase orders on file`} onClick={() => navigate('/field/procurement/orders')} />
              <FieldMenuBar label="Delivery schedule & at-risk supplies" detail={`${scheduledOrders.length} scheduled orders · ${atRisk.length} supplies at risk`} onClick={() => navigate('/field/procurement/at-risk')} />
              <FieldMenuBar label="Suppliers database" detail="Supplier details, contacts and delivery history" onClick={() => navigate('/field/procurement/suppliers')} />
            </Stack>
          )}

          {activeTab === 'pos' && (
            <Stack spacing={2}>
              <Typography variant="h4" sx={{ fontSize: 30, fontWeight: 900, letterSpacing: 0 }}>Procurement</Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={900}>Current Orders</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} disabled={!activeProjectId}>
                  Create new
                </Button>
              </Stack>
              {lastCreatedPo && (
                <Alert
                  severity="success"
                  action={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        color="inherit"
                        size="small"
                        startIcon={<UndoIcon />}
                        disabled={undoingId === lastCreatedPo.id}
                        onClick={() => undoOrder(lastCreatedPo.id)}
                      >
                        {undoingId === lastCreatedPo.id ? 'Undoing…' : 'Undo'}
                      </Button>
                      <Button color="inherit" size="small" onClick={() => setLastCreatedPoId(null)}>Dismiss</Button>
                    </Stack>
                  }
                >
                  Order {lastCreatedPo.poNumber} created under Unscheduled Orders — it can still be edited or undone below.
                </Alert>
              )}
              <Typography variant="subtitle2" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Unscheduled Orders
              </Typography>
              <Stack spacing={1}>
                {unscheduledOrders.map((po) => renderOrderRow(po, 'uns', 'Order'))}
                {unscheduledOrders.length === 0 && <Alert severity="success">No unscheduled orders — every current order has a delivery date.</Alert>}
              </Stack>
              <Typography variant="subtitle2" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Scheduled Orders
              </Typography>
              <Stack spacing={1}>
                {scheduledOrders.map((po) => renderOrderRow(po, 'sch', 'Purchase Order', true))}
                {scheduledOrders.length === 0 && <Alert severity="info">No scheduled orders yet.</Alert>}
              </Stack>
              <Typography variant="h6" fontWeight={900}>Completed Order History</Typography>
              {historyFilterBar}
              {renderCompletedOrderHistory()}
            </Stack>
          )}

          {activeTab === 'at-risk' && (
            <Stack spacing={2}>
              <Typography variant="h4" sx={{ fontSize: 30, fontWeight: 900, letterSpacing: 0 }}>Procurement</Typography>
              {materialsToRecoupPanel}
              <Typography variant="h6" sx={{ fontWeight: 900 }}>At Risk Supplies</Typography>
              <Stack spacing={1}>
                {atRisk.map((row, index) => renderSupplyRow(row, index))}
                {atRisk.length === 0 && <Alert severity="success">No supplies currently at procurement risk.</Alert>}
              </Stack>

              <Typography variant="h6" sx={{ fontWeight: 900 }}>Order schedule</Typography>
              {scheduleGroups.map(([date, orders]) => (
                <Paper key={date} variant="outlined" sx={{ borderRadius: 1, bgcolor: '#fff', overflow: 'hidden' }}>
                  <Typography sx={{ px: 1.5, py: 1, fontWeight: 900, borderBottom: '1px solid', borderColor: 'divider' }}>
                    Expected {date}
                  </Typography>
                  {orders.map((po) => (
                    <Stack
                      key={po.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      onClick={() => navigate(`/field/purchase-orders/${po.id}`)}
                      sx={{ px: 1.5, py: 1, cursor: 'pointer', borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900 }} noWrap>PO {po.poNumber}</Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {po.supplier?.name || supplierById.get(po.supplierId)?.name || po.supplierId}
                        </Typography>
                      </Box>
                      <Chip size="small" color={PO_STATUS_COLORS[po.status]} label={PO_STATUS_LABELS[po.status]} />
                      <ChevronRightIcon />
                    </Stack>
                  ))}
                </Paper>
              ))}
              {scheduleGroups.length === 0 && <Alert severity="info">No scheduled orders yet.</Alert>}
            </Stack>
          )}

          {activeTab === 'suppliers' && (
            <Stack spacing={2}>
              <Typography variant="h4" sx={{ fontSize: 30, fontWeight: 900, letterSpacing: 0 }}>Procurement</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 900 }}>Suppliers Database</Typography>
                {addSupplierButton}
              </Stack>
              {(catalog?.suppliers ?? []).map((supplier: any) => {
                const slippage = supplierSlippage.find((row) => row.supplierId === supplier.id);
                const supplierReceipts = receiptsForSupplier(supplier.id);
                const key = `field-supplier-${supplier.id}`;
                const expanded = expandedRow === key;
                return (
                  <Paper key={key} variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                    <Stack direction="row" spacing={1} alignItems="center" onClick={() => toggleRow(key)} sx={{ p: 1.5, cursor: 'pointer' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 900 }}>{supplier.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{supplierHistoryLabel(slippage)}</Typography>
                      </Box>
                      {supplier.isActive != null && (
                        <Chip size="small" color={supplier.isActive ? 'success' : 'default'} label={supplier.isActive ? 'Active' : 'Inactive'} />
                      )}
                      <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />
                    </Stack>
                    <Collapse in={expanded} unmountOnExit>
                      <Divider />
                      <Box sx={{ p: 1.5 }}>
                        <Typography sx={{ fontWeight: 900 }}>Contact info</Typography>
                        <Grid container spacing={1} sx={{ mt: 0 }}>
                          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Contact</Typography>{supplierField(supplier.contactPerson)}</Grid>
                          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Phone</Typography>{supplierField(supplier.phone)}</Grid>
                          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Email</Typography>{supplierField(supplier.email)}</Grid>
                          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Address</Typography>{supplierField(supplier.address)}</Grid>
                        </Grid>
                        <Typography sx={{ mt: 1, fontWeight: 900 }}>Terms</Typography>
                        {supplierField(supplier.paymentTerms)}
                        <Typography sx={{ mt: 1, fontWeight: 900 }}>Delivery performance</Typography>
                        <Typography variant="body2">
                          {slippage
                            ? `${slippage.orderCount ?? 0} POs tracked · ${slippage.openOrderCount ?? 0} open · ${slippage.receivedOrderCount ?? 0} received · ${slippage.lateOpenOrderCount ?? 0} late open`
                            : 'No purchase order history yet'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {supplierReceiptTimingLabel(slippage)}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          disabled={supplierReceipts.length === 0}
                          onClick={() => setReceiptSupplier(supplier)}
                          sx={{ mt: 1 }}
                        >
                          View receipts ({supplierReceipts.length})
                        </Button>
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
              {(catalog?.suppliers ?? []).length === 0 && <Alert severity="info">No suppliers on file yet.</Alert>}
            </Stack>
          )}
        </Stack>
      )}

      {!fieldMode && <Paper sx={{ borderRadius: 1 }}>
        {!fieldMode && <Tabs value={tab} onChange={(_, value: TabKey) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="tasks" label={`Role Tasks (${roleTasks.length})`} />
          <Tab value="pos" label="Purchase Orders" />
          <Tab value="grns" label="Order History" />
          <Tab value="at-risk" label="At Risk" />
          <Tab value="suppliers" label="Suppliers Database" />
        </Tabs>}
        <Box sx={{ p: 2 }}>
          {activeTab === 'tasks' && (
            <RoleTaskQueue rows={roleTasks} onComplete={completeRoleTask} completingId={completingId} />
          )}

          {activeTab === 'pos' && (
            <Stack spacing={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>PO Number</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Expected</TableCell>
                    <TableCell align="right">Days Slipped</TableCell>
                    <TableCell align="right">Total Value</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseOrders.map((po: PurchaseOrder) => {
                    const slipped = daysSlipped(po);
                    const progress = statusProgress(po);
                    return (
                      <TableRow key={po.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>{po.poNumber}</Typography>
                        </TableCell>
                        <TableCell>{po.supplier?.name || supplierById.get(po.supplierId)?.name || po.supplierId}</TableCell>
                        <TableCell><Chip size="small" color={PO_STATUS_COLORS[po.status]} label={PO_STATUS_LABELS[po.status]} /></TableCell>
                        <TableCell>
                          <Typography variant="caption">Sourced {progress.sourced}% · Approved {progress.approved}% · Paid {progress.paid}% · Shipped {progress.shipped}% · Delivered {progress.delivered}%</Typography>
                        </TableCell>
                        <TableCell>{formatDate(po.expectedDeliveryDate)}</TableCell>
                        <TableCell align="right">{slipped === null ? '-' : slipped}</TableCell>
                        <TableCell align="right">{formatCurrency(Number(po.totalAmount || 0))}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Open details">
                            <IconButton component={RouterLink} to={fieldMode ? `/field/purchase-orders/${po.id}` : `/procurement/${po.id}`} size="small">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading.purchaseOrders && purchaseOrders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}><Alert severity="info">No purchase orders match the current filters.</Alert></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                {renderCompletedOrderHistory()}
              </Paper>
            </Stack>
          )}

          {activeTab === 'grns' && (
            renderReceiptGroups(receiptGroups, 'No delivery records loaded.')
          )}

          {activeTab === 'at-risk' && (
            <Stack spacing={2}>
              {materialsToRecoupPanel}
              <Grid container spacing={2}>
              {atRisk.map((row, index) => {
                const linkedPo = row.purchaseOrderId ? purchaseOrders.find((po) => po.id === row.purchaseOrderId) : undefined;
                const shortfall = shortfallOf(row);
                const procurementSpec = procurementSpecOf(row);
                return (
                  <Grid item xs={12} md={6} key={`${row.taskId}-${row.resourceId}-${index}`}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Typography fontWeight={900}>PO item: {row.resourceName || resourceById.get(row.resourceId)?.name || row.resourceId}</Typography>
                          <Chip size="small" color={RISK_STATUS_COLORS[row.procurementStatus]} label={row.procurementStatus.replace(/_/g, ' ')} />
                        </Stack>
                        <Typography variant="body2">Task: {row.taskCode || row.taskTitle || row.taskId}</Typography>
                        <Typography variant="body2">Required: {row.required} · Shortfall: {shortfall}</Typography>
                        {row.procurementCoveredQuantity != null && (
                          <Typography variant="body2">Covered by open orders: {Number(row.procurementCoveredQuantity)}</Typography>
                        )}
                        <Typography variant="body2">Scheduled start: {formatDate(row.plannedStartDate)}</Typography>
                        <Typography variant="body2">Expected delivery: {formatDate(row.expectedDeliveryDate)}</Typography>
                        {procurementSpec && (
                          <Typography variant="body2">
                            <strong>Procurement specification:</strong> {procurementSpec}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          Purchase order: {linkedPo ? `${linkedPo.poNumber} (${PO_STATUS_LABELS[linkedPo.status]})` : 'Not ordered'}
                        </Typography>
                        <Button variant="contained" onClick={() => setAtRiskDetail(row)}>View details</Button>
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
              {atRisk.length === 0 && (
                <Grid item xs={12}><Alert severity="success">No task resources at procurement risk within the window.</Alert></Grid>
              )}
              </Grid>
            </Stack>
          )}

          {activeTab === 'suppliers' && suppliersSection}
        </Box>
      </Paper>}

      <Dialog open={Boolean(atRiskDetail)} onClose={() => setAtRiskDetail(null)} maxWidth="sm" fullWidth>
        {atRiskDetail && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={900} noWrap>
                    Resource # {atRiskResource?.resourceCode || atRiskDetail.resourceName || atRiskDetail.resourceId}
                    <Typography component="span" color="text.secondary" fontWeight={700}> — {atRiskType}</Typography>
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color={RISK_STATUS_COLORS[atRiskDetail.procurementStatus]}
                  label={atRiskDetail.procurementStatus.replace(/_/g, ' ')}
                />
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }} color="text.secondary">
                    Material / Item
                  </Typography>
                  <Typography fontWeight={800} sx={{ mt: 0.5 }}>{atRiskDetail.resourceName || atRiskResource?.name || atRiskDetail.resourceId}</Typography>
                  <Grid container spacing={1} sx={{ mt: 0.25 }}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Unit</Typography>
                      <Typography variant="body2" fontWeight={700}>{atRiskResource?.unitOfMeasure || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Unit cost</Typography>
                      <Typography variant="body2" fontWeight={700}>{formatCurrency(Number(atRiskResource?.unitCost || 0))}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Required</Typography>
                      <Typography variant="body2" fontWeight={700}>{atRiskDetail.required}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Shortfall</Typography>
                      <Typography variant="body2" fontWeight={700} color={shortfallOf(atRiskDetail) > 0 ? 'error.main' : 'text.primary'}>
                        {shortfallOf(atRiskDetail)} of {atRiskDetail.required}
                      </Typography>
                      {atRiskDetail.procurementCoveredQuantity != null && (
                        <Typography variant="caption" color="text.secondary">
                          {Number(atRiskDetail.procurementCoveredQuantity)} covered by open orders
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                {procurementSpecOf(atRiskDetail) && (
                  <>
                    <Divider />

                    <Box>
                      <Typography variant="subtitle2" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }} color="text.secondary">
                        Procurement Specification
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {procurementSpecOf(atRiskDetail)}
                      </Typography>
                    </Box>
                  </>
                )}

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }} color="text.secondary">
                    Needed for
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 0.25 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Task</Typography>
                      <Typography variant="body2" fontWeight={700}>{atRiskDetail.taskCode || atRiskDetail.taskTitle || atRiskDetail.taskId}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Starts</Typography>
                      <Typography variant="body2" fontWeight={700}>{formatDate(atRiskDetail.plannedStartDate)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Expected delivery</Typography>
                      <Typography variant="body2" fontWeight={700}>{formatDate(atRiskDetail.expectedDeliveryDate)}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }} color="text.secondary">
                    Supplier — purchased from
                  </Typography>
                  {atRiskSupplier ? (
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                      <Typography fontWeight={800}>{(atRiskSupplier as any).name}</Typography>
                      {!atRiskPo && (
                        <Alert severity="info" sx={{ py: 0 }}>Preferred supplier — this item has not been ordered yet.</Alert>
                      )}
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Contact</Typography>
                          {supplierField((atRiskSupplier as any).contactPerson)}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Phone</Typography>
                          {supplierField((atRiskSupplier as any).phone)}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Email</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-all' }} color={(atRiskSupplier as any).email ? 'text.primary' : 'text.secondary'}>
                            {(atRiskSupplier as any).email || NOT_ON_FILE}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Payment terms</Typography>
                          {supplierField((atRiskSupplier as any).paymentTerms)}
                        </Grid>
                      </Grid>
                      {atRiskPo && (
                        <Paper variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 1.5 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Box>
                              <Typography variant="body2" fontWeight={800}>Purchase Order # {atRiskPo.poNumber}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Ordered {formatDate(atRiskPo.createdAt)} · Expected {formatDate(atRiskPo.expectedDeliveryDate)}
                              </Typography>
                            </Box>
                            <Chip size="small" label={PO_STATUS_LABELS[atRiskPo.status]} />
                          </Stack>
                        </Paper>
                      )}
                    </Stack>
                  ) : (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Not ordered yet — no supplier on record for this item.
                    </Alert>
                  )}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAtRiskDetail(null)}>Close</Button>
              {atRiskPo ? (
                <Button
                  variant="contained"
                  onClick={() => { setAtRiskDetail(null); navigate(`/field/purchase-orders/${atRiskPo.id}`); }}
                >
                  Open purchase order
                </Button>
              ) : (
                shortfallOf(atRiskDetail) > 0 && (
                  <Button
                    variant="contained"
                    disabled={!activeProjectId}
                    onClick={() => startOrderForShortage(atRiskDetail)}
                  >
                    Create order
                  </Button>
                )
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={Boolean(receiptSupplier)}
        onClose={() => {
          setReceiptSupplier(null);
          setReceiptSearch('');
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{receiptSupplier ? `${receiptSupplier.name} receipts` : 'Supplier receipts'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                size="small"
                label="Search receipts"
                value={receiptSearch}
                onChange={(event) => setReceiptSearch(event.target.value)}
                sx={{ flex: 1 }}
              />
              <Chip
                size="small"
                label={`${visibleSupplierReceipts.length} of ${selectedSupplierReceipts.length} receipts`}
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
              />
            </Stack>
            {selectedSupplierReceipts.length === 0 && (
              <Alert severity="info">No receipts are recorded for this supplier yet.</Alert>
            )}
            {selectedSupplierReceipts.length > 0 && visibleSupplierReceipts.length === 0 && (
              <Alert severity="info">No receipts match the current search.</Alert>
            )}
            {visibleSupplierReceipts.length > 0 && (
              renderReceiptGroups(groupReceiptsByMonth(visibleSupplierReceipts), 'No receipts match the current search.')
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setReceiptSupplier(null);
            setReceiptSearch('');
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(recoupDetail)} onClose={() => setRecoupDetail(null)} maxWidth="sm" fullWidth>
        {recoupDetail && (() => {
          const supplier = supplierForResource(recoupDetail.resourceId);
          const shippingTime = shippingTimeForResource(recoupDetail.resourceId, supplier?.id);
          const target = recoupDetail.reorderLevel ?? recoupDetail.safetyStockLevel ?? 0;
          const recoupQuantity = target > 0
            ? Math.max(0, target - Number(recoupDetail.quantityAvailable || 0))
            : Math.max(0, Number(recoupDetail.quantityInUse || 0));
          const usedPercent = usedPercentOf(recoupDetail);
          const estimatedCost = Number(recoupDetail.unitCost || 0) * recoupQuantity;
          return (
            <>
              <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">
                      Recoup detail
                    </Typography>
                    <Typography variant="h6" fontWeight={900}>{recoupDetail.resourceName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {recoupDetail.resourceCode} - {humanize(recoupDetail.resourceType)} - {recoupDetail.storageLocationName}
                    </Typography>
                  </Box>
                  <Chip color={usedPercent >= 90 ? 'error' : 'warning'} label={`${usedPercent}% used`} />
                </Stack>
              </DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  <Grid container spacing={1.25}>
                    <Grid item xs={6}>
                      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">Available</Typography>
                        <Typography fontWeight={900}>{formatQuantity(recoupDetail.quantityAvailable, recoupDetail.unitOfMeasure)}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">Used</Typography>
                        <Typography fontWeight={900}>{formatQuantity(recoupDetail.quantityInUse, recoupDetail.unitOfMeasure)}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">Total stock basis</Typography>
                        <Typography fontWeight={900}>{formatQuantity(recoupDetail.totalQuantity, recoupDetail.unitOfMeasure)}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">Recoup quantity</Typography>
                        <Typography fontWeight={900} color={usedPercent >= 90 ? 'error.main' : 'warning.main'}>
                          {formatQuantity(recoupQuantity, recoupDetail.unitOfMeasure)}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">
                      Supplier and shipping
                    </Typography>
                    <Typography fontWeight={800}>{supplier?.name || 'No preferred supplier'}</Typography>
                    <Typography variant="body2" color="text.secondary">{shippingTime}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">
                      Procurement basis
                    </Typography>
                    <Typography variant="body2">
                      Recoup is triggered because Stores usage reached {usedPercent}%, above the 75% threshold.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reorder level: {recoupDetail.reorderLevel ?? 'Not set'} | Safety stock: {recoupDetail.safetyStockLevel ?? 'Not set'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Estimated recoup value: {estimatedCost > 0 ? formatCurrency(estimatedCost) : 'No unit cost recorded'}
                    </Typography>
                  </Box>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRecoupDetail(null)}>Close</Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setRecoupDetail(null);
                    setCreateOpen(true);
                    setPoForm((current) => ({
                      ...current,
                      supplierId: supplier?.id ?? current.supplierId,
                      items: [{
                        resourceId: recoupDetail.resourceId,
                        unitOfMeasure: recoupDetail.unitOfMeasure || '',
                        quantityOrdered: Math.max(1, recoupQuantity),
                        unitCost: Number(recoupDetail.unitCost || 0),
                        expectedDeliveryDate: current.items[0]?.expectedDeliveryDate || expectedDateForResource(recoupDetail.resourceId, supplier?.id),
                      }],
                    }));
                  }}
                >
                  Start PO
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Create new PO</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Supplier</InputLabel>
                  <Select
                    value={poForm.supplierId}
                    label="Supplier"
                    onChange={(event) => {
                      const supplierId = event.target.value;
                      setPoForm((current) => ({
                        ...current,
                        supplierId,
                        items: current.items.map((line) => ({
                          ...line,
                          expectedDeliveryDate: line.expectedDeliveryDate || (line.resourceId ? expectedDateForResource(line.resourceId, supplierId) : ''),
                        })),
                      }));
                    }}
                  >
                    {(catalog?.suppliers ?? []).map((supplier) => (
                      <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="PO number" value={poForm.poNumber} onChange={(event) => setPoForm({ ...poForm, poNumber: event.target.value })} />
              </Grid>
            </Grid>
            <TextField fullWidth label="Notes" value={poForm.notes} onChange={(event) => setPoForm({ ...poForm, notes: event.target.value })} multiline minRows={2} />
            {!hasValidPo && (
              <Alert severity="info">
                Each PO item needs a material, unit, quantity, and expected delivery date before the order can be created for Stores receiving.
              </Alert>
            )}
            {hasDuplicatePoItems && (
              <Alert severity="warning">
                This resource is already on the PO. Update the quantity on the existing row instead of adding it again.
              </Alert>
            )}
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" fontWeight={700}>Items</Typography>
                <Button startIcon={<AddIcon />} onClick={() => setPoForm((current) => ({ ...current, items: [...current.items, newLine()] }))}>
                  Add item
                </Button>
              </Stack>
              {poForm.items.map((line, index) => {
                const selectedOnOtherRows = new Set(poForm.items.map((row, i) => (i === index ? '' : row.resourceId)).filter(Boolean));
                return (
                <Grid container spacing={1} key={index} alignItems="center" sx={{ p: 1, border: '1px solid', borderColor: duplicatePoResourceIds.has(line.resourceId) ? 'warning.main' : 'divider', borderRadius: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      size="small"
                      options={catalog?.resources ?? []}
                      value={(catalog?.resources ?? []).find((resource) => resource.id === line.resourceId) ?? null}
                      getOptionLabel={(resource) => `${resource.resourceCode} - ${resource.name}`}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionDisabled={(resource) => selectedOnOtherRows.has(resource.id)}
                      onChange={(_, resource) => {
                        updateLine(index, {
                          resourceId: resource?.id ?? '',
                          unitOfMeasure: resource?.unitOfMeasure || '',
                          unitCost: Number(resource?.unitCost || line.unitCost || 0),
                          expectedDeliveryDate: resource ? line.expectedDeliveryDate || expectedDateForResource(resource.id, poForm.supplierId) : line.expectedDeliveryDate,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Item"
                          placeholder="Search material"
                        />
                      )}
                    />
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => openCreateMaterialDialog(index)}
                      sx={{ mt: 0.5, px: 0, fontWeight: 800 }}
                    >
                      Create material
                    </Button>
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <TextField size="small" type="number" label="Qty" value={line.quantityOrdered} onChange={(event) => updateLine(index, { quantityOrdered: Number(event.target.value) })} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={unitOptions}
                      value={line.unitOfMeasure || ''}
                      onInputChange={(_, value) => updateLine(index, { unitOfMeasure: value })}
                      onChange={(_, value) => updateLine(index, { unitOfMeasure: value || '' })}
                      renderInput={(params) => <TextField {...params} label="Unit" placeholder="Select unit" />}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField size="small" type="number" label="Price" value={line.unitCost} onChange={(event) => updateLine(index, { unitCost: Number(event.target.value) })} />
                  </Grid>
                  <Grid item xs={10} md={3}>
                    <TextField size="small" fullWidth required type="date" label="Expected" value={line.expectedDeliveryDate} InputLabelProps={{ shrink: true }} onChange={(event) => updateLine(index, { expectedDeliveryDate: event.target.value })} />
                  </Grid>
                  <Grid item xs={2} md={1}>
                    <Tooltip title="Remove item">
                      <span>
                        <IconButton disabled={poForm.items.length === 1} onClick={() => setPoForm((current) => ({ ...current, items: current.items.filter((_, i) => i !== index) }))}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
                );
              })}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePo} disabled={!canCreatePo || saving}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={materialDialog.open} onClose={closeCreateMaterialDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create material</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Material name"
              value={materialDialog.name}
              onChange={(event) => setMaterialDialog((current) => ({ ...current, name: event.target.value }))}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={materialDialog.resourceType}
                    label="Type"
                    onChange={(event) => setMaterialDialog((current) => ({ ...current, resourceType: event.target.value as typeof current.resourceType }))}
                  >
                    <MenuItem value="material">Material</MenuItem>
                    <MenuItem value="supply">Supply</MenuItem>
                    <MenuItem value="tool">Tool</MenuItem>
                    <MenuItem value="equipment">Equipment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={unitOptions}
                  value={materialDialog.unitOfMeasure}
                  onInputChange={(_, value) => setMaterialDialog((current) => ({ ...current, unitOfMeasure: value }))}
                  onChange={(_, value) => setMaterialDialog((current) => ({ ...current, unitOfMeasure: value || '' }))}
                  renderInput={(params) => <TextField {...params} label="Unit" />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Category"
                  value={materialDialog.category}
                  onChange={(event) => setMaterialDialog((current) => ({ ...current, category: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Unit price"
                  value={materialDialog.unitCost}
                  onChange={(event) => setMaterialDialog((current) => ({ ...current, unitCost: Number(event.target.value) }))}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateMaterialDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={createMaterialForLine}
            disabled={saving || !materialDialog.name.trim() || !materialDialog.unitOfMeasure.trim()}
          >
            Add to PO
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
